import { useContext } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Builder primitive — a publish / unpublish checkbox bound to a boolean store
 * path (typically a listing row's `status`).
 *
 * On change it optimistically flips the bound value in the store (instant UI
 * feedback, no refetch) then persists the new state through decoupled_toolbox's
 * generic write API :
 *
 *   POST /decoupled-api/save?display=decoupled&entity_type=node
 *        &bundle=<row.type>&entity_id=<row.id>
 *   body: { data: { status: <new boolean> } }
 *
 * CSRF + credentials are injected automatically by `fetchPatch.js` for any
 * `/decoupled-api/*` mutation, so no header wiring is needed here. If the
 * request fails the optimistic flip is rolled back.
 *
 * Server-side prerequisite : the entity's `decoupled` view display must expose
 * the `status` field (decoupled_boolean formatter, `decoupled_field_key: status`)
 * in its content region, with write access enabled — otherwise the field is
 * silently skipped on save.
 *
 *   - type: PublishToggle
 *     dataLocation: ~.status      # boolean — the row's publication state
 *     idLocation: ~.id            # optional, default ~.id   (entity id)
 *     bundleLocation: ~.type      # optional, default ~.type (entity bundle)
 */
export const PublishToggle = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const { formData: statusValue, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: false,
    globalDataContext,
    templateContext,
  })

  const { formData: entityId } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.idLocation ?? '~.id',
    defaultValue: null,
    globalDataContext,
    templateContext,
  })

  // `bundle` may be a static literal (e.g. 'user', which has no per-row type)
  // or resolved from a row field via `bundleLocation` (default ~.type).
  const { formData: resolvedBundle } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.bundleLocation ?? '~.type',
    defaultValue: null,
    globalDataContext,
    templateContext,
  })
  const bundle = props.bundle ?? resolvedBundle

  // ES raw _source gives a real boolean; tolerate 1/0/"1"/"0" defensively.
  const published = statusValue === true || statusValue === 1 || statusValue === '1'

  const entityType = props.entityType ?? 'node'
  const display = props.display ?? 'decoupled'
  const statusKey = props.statusKey ?? 'status'
  const saveUrl = props.saveUrl ?? '/decoupled-api/save'

  const publishedLabel = props.publishedLabel ?? 'Publié'
  const unpublishedLabel = props.unpublishedLabel ?? 'Non publié'

  const toggle = () => {
    const next = !published

    // Optimistic flip — instant feedback, persisted just below.
    globalDataContext.updateData(next, formDataPath)

    if (entityId == null || !bundle) {
      console.error('PublishToggle: missing entity id or bundle, cannot persist status.')
      return
    }

    const url =
      `${saveUrl}?display=${encodeURIComponent(display)}` +
      `&entity_type=${encodeURIComponent(entityType)}` +
      `&bundle=${encodeURIComponent(bundle)}` +
      `&entity_id=${encodeURIComponent(entityId)}`

    // CSRF + credentials injected by fetchPatch.js for any /decoupled-api/* POST.
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: { [statusKey]: next } }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
      })
      .catch((err) => {
        // Roll back the optimistic flip on failure.
        globalDataContext.updateData(published, formDataPath)
        console.error('PublishToggle: failed to persist status', err)
      })
  }

  return (
    <label
      title={published ? 'Cliquer pour dépublier' : 'Cliquer pour publier'}
      className="inline-flex items-center gap-2 cursor-pointer"
    >
      <input
        type="checkbox"
        checked={published}
        onChange={toggle}
        className="w-4 h-4 rounded border-gray-300 text-green-600 accent-green-600 cursor-pointer"
      />
      <span className={`text-xs font-medium ${published ? 'text-green-700' : 'text-gray-500'}`}>
        {published ? publishedLabel : unpublishedLabel}
      </span>
    </label>
  )
}
